import React, { FC, ReactNode, useCallback, useMemo } from 'react';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useController } from 'react-hook-form';
import classNames from 'classnames';
import Select, { Props, SingleValue, StylesConfig } from 'react-select';

import { Maybe } from 'interfaces/graphql-typings';

import styles from './fieldStyles.scss';

export const generateSelectStyles = ({
  error,
  size
}: {
  error?: boolean;
  size: 'small' | 'medium' | 'large';
}): StylesConfig => ({
  control: base => ({
    ...base,
    borderRadius: '0.425rem',
    borderWidth: error ? 2 : 1,
    minHeight: 'auto',
    padding: 0,
    ['&:not(:focus-within)']: {
      borderColor: error ? '#d51800' : '#cccccc'
    }
  }),
  valueContainer: base => ({
    ...base,
    padding: size === 'small' ? '6px 11px' : size === 'medium' ? '7px 12px' : '8px 13px',
    gap: size === 'small' ? 5 : size === 'medium' ? 8 : 10
  }),
  dropdownIndicator: base => ({
    ...base,
    padding: size === 'small' ? '6px 11px' : size === 'medium' ? '7px 12px' : '8px 13px',
    paddingLeft: '0'
  }),
  clearIndicator: base => ({
    ...base,
    padding: size === 'small' ? '6px 11px' : size === 'medium' ? '7px 12px' : '8px 13px',
    paddingLeft: '0'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  input: base => ({ ...base, margin: 0, padding: 0 }),
  multiValue: base => ({ ...base, margin: 0 })
});

type Option = { label: string; value: Maybe<string> };

const SelectField: FC<{
  disabled?: boolean;
  generateHelperElement?: (selectedOption: Maybe<Option>) => ReactNode;
  label?: string;
  name: string;
  onChangeCallback?: (value: string) => void;
  options: Option[];
  size?: 'small' | 'medium' | 'large';
} & Pick<Props, 'isClearable' | 'isSearchable' | 'placeholder'>> = ({
  disabled,
  generateHelperElement,
  isClearable,
  isSearchable = false,
  label,
  name,
  onChangeCallback,
  options,
  placeholder,
  size = 'medium'
}) => {
  const {
    field: { onChange, value, ...field },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ name });

  const isDisabled = disabled || isSubmitting;

  const selectedOption = useMemo(() => options.find(option => option.value === value) || null, [
    options,
    value
  ]);

  const handleOnChange = useCallback(
    (option: SingleValue<Option>) => {
      onChange(option?.value || '');
      if (onChangeCallback) onChangeCallback(option?.value || '');
    },
    [onChange, onChangeCallback]
  );

  const selectStyles = useMemo(
    () => generateSelectStyles({ error: Boolean(error), size }) as StylesConfig<Option, false>,
    [error, size]
  );

  return (
    <div
      className={classNames(styles.field, {
        [styles.small]: size === 'small',
        [styles.medium]: size === 'medium',
        [styles.large]: size === 'large'
      })}
    >
      {label ? (
        <label data-title htmlFor={name}>
          {label}
        </label>
      ) : null}
      <Select
        {...field}
        inputId={name}
        classNamePrefix={name}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        onChange={handleOnChange}
        options={options}
        placeholder={placeholder}
        styles={selectStyles}
        value={selectedOption}
      />
      {error?.message ? (
        <label data-error htmlFor={name}>
          <FontAwesomeIcon icon={faExclamationCircle} />
          {error.message}
        </label>
      ) : null}
      {generateHelperElement ? generateHelperElement(selectedOption) : null}
    </div>
  );
};

export default SelectField;
